import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import {
  Cog6ToothIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  BellIcon,
} from '@heroicons/react/24/solid';
import Header from '../common/Header';
import Footer from '../common/Footer';
import { authAPI } from '../../services/api';

const sectionCard: React.CSSProperties = {
  borderRadius: '16px',
  border: '1px solid #e5e7eb',
  overflow: 'hidden',
};

const sectionHeader: React.CSSProperties = {
  background: 'linear-gradient(135deg, #eff6ff 0%, #eef2ff 60%, #f0f9ff 100%)',
  padding: '20px 24px 16px',
  borderBottom: '1px solid #e5e7eb',
};

const sectionTitle: React.CSSProperties = {
  margin: 0,
  fontSize: '1rem',
  fontWeight: 700,
  color: '#1f2937',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const sectionSubtitle: React.CSSProperties = {
  margin: '4px 0 0',
  fontSize: '0.82rem',
  color: '#6b7280',
  lineHeight: 1.4,
};

const iconBubble = (bg: string): React.CSSProperties => ({
  width: '32px',
  height: '32px',
  borderRadius: '10px',
  background: bg,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const CustomerSettings: React.FC = () => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);

  // Notification preferences state
  const [prefs, setPrefs] = useState({
    notify_order_updates_email: true,
    notify_order_updates_sms: false,
    notify_order_confirmations_email: true,
    notify_order_confirmations_sms: false,
    notify_promotions_email: false,
    notify_promotions_sms: false,
  });
  const [prefsLoading, setPrefLoading] = useState(true);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsMessage, setPrefsMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);
  const [hasSmsPackage, setHasSmsPackage] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await authAPI.getNotificationPreferences();
        const { has_sms_package, sms_enabled, ...prefData } = res.data;
        setPrefs(p => ({ ...p, ...prefData }));
        setHasSmsPackage(!!has_sms_package);
        setSmsEnabled(!!sms_enabled);
      } catch { /* use defaults */ }
      finally { setPrefLoading(false); }
    };
    load();
  }, []);

  const handleTogglePref = async (field: string, value: boolean) => {
    setPrefs(p => ({ ...p, [field]: value }));
    setPrefsMessage(null);
    setPrefsSaving(true);
    try {
      await authAPI.updateNotificationPreferences({ [field]: value });
      setPrefsMessage({ type: 'success', text: 'Preference updated.' });
    } catch {
      setPrefs(p => ({ ...p, [field]: !value }));
      setPrefsMessage({ type: 'danger', text: 'Failed to update preference.' });
    } finally { setPrefsSaving(false); }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!oldPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'danger', text: 'Please fill in all fields.' });
      return;
    }
    if (newPassword.length < 8) {
      setMessage({ type: 'danger', text: 'New password must be at least 8 characters.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'danger', text: 'New passwords do not match.' });
      return;
    }

    setSaving(true);
    try {
      await authAPI.changePassword(oldPassword, newPassword);
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to change password.';
      setMessage({ type: 'danger', text: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Header />
      <Container fluid className="py-4 px-3 px-md-4 page-content-padding" style={{ maxWidth: '720px' }}>

        {/* Page Header */}
        <div className="d-flex align-items-center gap-2 mb-4">
          <div style={iconBubble('linear-gradient(135deg, #1e3a8a, #3b82f6)')}>
            <Cog6ToothIcon style={{ width: '18px', height: '18px', color: '#fff' }} />
          </div>
          <div>
            <h5 className="fw-bold mb-0" style={{ color: '#1f2937', fontSize: '1.2rem' }}>Settings</h5>
            <p className="mb-0" style={{ fontSize: '0.78rem', color: '#6b7280' }}>Manage your account preferences</p>
          </div>
        </div>

        {/* Change Password Section */}
        <Card style={sectionCard} className="mb-4">
          <div style={sectionHeader}>
            <h6 style={sectionTitle}>
              <div style={iconBubble('#1e40af')}>
                <LockClosedIcon style={{ width: '15px', height: '15px', color: '#fff' }} />
              </div>
              Change Password
            </h6>
            <p style={sectionSubtitle}>Update your account password</p>
          </div>
          <Card.Body style={{ padding: '24px' }}>
            {message && (
              <Alert
                variant={message.type}
                className="py-2 px-3 mb-3 d-flex align-items-center gap-2"
                style={{ fontSize: '0.84rem', borderRadius: '10px' }}
                dismissible
                onClose={() => setMessage(null)}
              >
                {message.type === 'success' && <CheckCircleIcon style={{ width: '16px', height: '16px' }} />}
                {message.text}
              </Alert>
            )}
            <Form onSubmit={handleChangePassword}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem', color: '#374151' }}>Current Password</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={showOld ? 'text' : 'password'}
                    value={oldPassword}
                    onChange={e => setOldPassword(e.target.value)}
                    placeholder="Enter current password"
                    style={{ borderRadius: '10px', padding: '10px 40px 10px 14px' }}
                  />
                  <button
                    type="button"
                    className="position-absolute"
                    style={{ right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    onClick={() => setShowOld(!showOld)}
                  >
                    {showOld
                      ? <EyeSlashIcon style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
                      : <EyeIcon style={{ width: '16px', height: '16px', color: '#9ca3af' }} />}
                  </button>
                </div>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem', color: '#374151' }}>New Password</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 8 characters)"
                    style={{ borderRadius: '10px', padding: '10px 40px 10px 14px' }}
                  />
                  <button
                    type="button"
                    className="position-absolute"
                    style={{ right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    onClick={() => setShowNew(!showNew)}
                  >
                    {showNew
                      ? <EyeSlashIcon style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
                      : <EyeIcon style={{ width: '16px', height: '16px', color: '#9ca3af' }} />}
                  </button>
                </div>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem', color: '#374151' }}>Confirm New Password</Form.Label>
                <Form.Control
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  style={{ borderRadius: '10px', padding: '10px 14px' }}
                />
              </Form.Group>
              <Button
                type="submit"
                disabled={saving}
                style={{
                  background: 'linear-gradient(135deg, #1e3a8a, #2563eb)',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '10px 28px',
                  fontWeight: 600,
                  fontSize: '0.88rem',
                }}
              >
                {saving ? <><Spinner size="sm" className="me-2" />Saving...</> : 'Update Password'}
              </Button>
            </Form>
          </Card.Body>
        </Card>

        {/* Communication Preferences */}
        <Card style={sectionCard} className="mb-4">
          <div style={sectionHeader}>
            <h6 style={sectionTitle}>
              <div style={iconBubble('#1e40af')}>
                <BellIcon style={{ width: '15px', height: '15px', color: '#fff' }} />
              </div>
              Communication Preferences
            </h6>
            <p style={sectionSubtitle}>Choose which notifications you'd like to receive</p>
          </div>
          <Card.Body style={{ padding: '24px' }}>
            {prefsMessage && (
              <Alert variant={prefsMessage.type} className="py-2 px-3 mb-3" style={{ fontSize: '0.84rem', borderRadius: '10px' }}
                dismissible onClose={() => setPrefsMessage(null)}>
                {prefsMessage.type === 'success' && <CheckCircleIcon style={{ width: '16px', height: '16px', display: 'inline', marginRight: '4px' }} />}
                {prefsMessage.text}
              </Alert>
            )}
            {prefsLoading ? (
              <div className="text-center py-3"><Spinner size="sm" /></div>
            ) : (
              <div>
                {([
                  { emailKey: 'notify_order_confirmations_email' as const, smsKey: 'notify_order_confirmations_sms' as const, label: 'Order Confirmations', desc: 'Get notified when your order is placed' },
                  { emailKey: 'notify_order_updates_email' as const, smsKey: 'notify_order_updates_sms' as const, label: 'Order Status Updates', desc: 'Get notified when your order status changes' },
                  { emailKey: 'notify_promotions_email' as const, smsKey: 'notify_promotions_sms' as const, label: 'Promotions & News', desc: 'Receive promotional offers and company news' },
                ]).map(({ emailKey, smsKey, label, desc }) => (
                  <div key={emailKey} className="py-3" style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1f2937', marginBottom: '2px' }}>{label}</div>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '10px' }}>{desc}</div>
                    <div className="d-flex align-items-center gap-4">
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '0.78rem', color: '#374151', fontWeight: 500, minWidth: '65px' }}>📧 Email</span>
                        <Form.Check type="switch" id={`pref-${emailKey}`}
                          checked={prefs[emailKey]} onChange={e => handleTogglePref(emailKey, e.target.checked)}
                          disabled={prefsSaving} style={{ transform: 'scale(1.1)' }} />
                      </div>
                      {smsEnabled && (
                      <div className="d-flex align-items-center gap-2" title={!hasSmsPackage ? 'SMS package required — contact your company admin' : ''}>
                        <span style={{ fontSize: '0.78rem', color: hasSmsPackage ? '#374151' : '#9ca3af', fontWeight: 500, minWidth: '75px' }}>💬 Text/SMS</span>
                        <Form.Check type="switch" id={`pref-${smsKey}`}
                          checked={prefs[smsKey]} onChange={e => handleTogglePref(smsKey, e.target.checked)}
                          disabled={prefsSaving || !hasSmsPackage} style={{ transform: 'scale(1.1)' }} />
                      </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
      <Footer />
    </>
  );
};

export default CustomerSettings;
